import { useState, useCallback, useEffect, useRef } from "react";
import { LoginScreen } from "./components/LoginScreen";
import { fetchAuthMode, isAuthenticated, logout, type AuthMode } from "./auth/webAuth";
import { MenuBar } from "./components/MenuBar";
import { isUiThemeId, setUiTheme } from "./theme/uiTheme";
import { TreeView, TreeNode } from "./components/TreeView";
import { SplitPane } from "./components/SplitPane";
import { TigerViewPanel } from "./components/TigerViewPanel";
import { openDataGridWindow, openDiagnosticsWindow } from "./utils/openToolWindows";
import { openGraphWindow, isGraphMode } from "./utils/openGraphWindow";
import { closeAllToolWindows, showAllToolWindows } from "./utils/closeToolWindows";
import { parseReportAction, dialogForReportModel } from "./utils/reportActions";
import { fetchCustomReportInfo } from "./api/customReportInfo";
import {
  generateCustomReport,
  reportWindowForRange,
  reportWindowForMonth,
  reportWindowForYear,
} from "./utils/excelReport";
import {
  generateControllerReport,
  generateEventReport,
  generateStabilityReport,
  generateToolReport,
} from "./utils/builtinReports";
import { getReportRange } from "./utils/reportDialogState";
import { getTigerResource } from "./utils/tigerResource";
import { initReportGraphRenderer } from "./utils/reportGraphRenderer";
import { useServerConnecting } from "./utils/useServerConnecting";
import { useStartupProgress } from "./utils/useStartupProgress";
import { importCsdFile } from "./utils/importCsd";
import {
  PickerKey,
  pickerInitialDir,
  pickerSavePath,
  rememberPickerDir,
} from "./utils/pickerFolder";

// Dialog imports - Core dialogs
import { DiagnosticsQueryDialog } from "./components/dialogs/DiagnosticsQueryDialog";
import { ExportDialog } from "./components/dialogs/ExportDialog";
import { BackupDataDialog } from "./components/dialogs/BackupDataDialog";
import { RestoreDataDialog } from "./components/dialogs/RestoreDataDialog";
import { GraphChannelSelectionDialog } from "./components/dialogs/GraphChannelSelectionDialog";
import { DataRequestDialog } from "./components/dialogs/DataRequestDialog";
import { VersionUpdateDialog } from "./components/dialogs/VersionUpdateDialog";
import { LicenseErrorDialog } from "./components/dialogs/LicenseErrorDialog";
import { DownloadProgressDialog } from "./components/dialogs/DownloadProgressDialog";
import { ReplayLoadDialog } from "./components/dialogs/ReplayLoadDialog";
import { ServerReplayDialog } from "./components/dialogs/ServerReplayDialog";
import { HistoricalProgressDialog } from "./components/dialogs/HistoricalProgressDialog";
import { ExportProgressDialog } from "./components/dialogs/ExportProgressDialog";
import { BackupRestoreProgressDialog } from "./components/dialogs/BackupRestoreProgressDialog";

// Dialog imports - Query/Filter dialogs
import { DiagFilterDialog } from "./components/dialogs/DiagFilterDialog";
import { DiagSelectTagDialog } from "./components/dialogs/DiagSelectTagDialog";

// Dialog imports - Data dialogs
import { LoadDataDialog } from "./components/dialogs/LoadDataDialog";
import { DiagnosticsMessagesPanel } from "./components/DiagnosticsMessagesPanel";
import { getLiveModeEnabled, getViewRunState, setViewMode, loadReplayData, replayDataFiles, getLiveConfig } from "./utils/viewMode";
import { scopeTreeToConfig, autoExpandViewsTree } from "./utils/liveScope";
import { startTiger } from "./utils/startTiger";
import { LOAD_DATA_REPLAY_EVENT, ReplayFromRequest } from "./utils/loadDataReplay";
import { dateTimeToTigerSeconds } from "./utils/tigerTime";

// Dialog imports - Time/Date selection
import { HistDiagSelectTagDialog } from "./components/dialogs/HistDiagSelectTagDialog";
import { HistTrendDiagSelectTagDialog } from "./components/dialogs/HistTrendDiagSelectTagDialog";

// Dialog imports - Report dialogs
import { ReportTimeDialog } from "./components/dialogs/ReportTimeDialog";
import { ReportDateDialog } from "./components/dialogs/ReportDateDialog";
import { ReportYearDialog } from "./components/dialogs/ReportYearDialog";
import { ReportMonthDialog } from "./components/dialogs/ReportMonthDialog";

// Dialog imports - Progress dialogs
import { UIProgressDialog } from "./components/dialogs/UIProgressDialog";

// Dialog imports - Utility/Admin
import { AboutDialog } from "./components/dialogs/AboutDialog";
import { ExitAppDialog } from "./components/dialogs/ExitAppDialog";
import { ChangePasswordDialog } from "./components/dialogs/ChangePasswordDialog";
import { ToolSetDialog } from "./components/dialogs/ToolSetDialog";
import { VersionInfoDialog } from "./components/dialogs/VersionInfoDialog";
import { RestartUIDialog } from "./components/dialogs/RestartUIDialog";

type DialogKey =
  | "diagnosticsQuery"
  | "export"
  | "exportTrend"
  | "diagnostics"
  | "graphChannelSelection"
  | "diagFilter"
  | "diagSelectTag"
  | "loadData"
  | "backupData"
  | "restoreData"
  | "histDiagSelectTag"
  | "histTrendDiagSelectTag"
  | "reportTime"
  | "reportDate"
  | "reportYear"
  | "reportMonth"
  | "uiProgress"
  | "about"
  | "exitApp"
  | "changePassword"
  | "toolSet"
  | "versionInfo"
  | "restartUI";

// --- Views tree types matching the Rust JSON shape ---
interface ViewEntry {
  m_view_name: string;
  m_view_id: string;
}

interface ViewGroup {
  m_group_name: string;
  m_group_id: string;
  views: ViewEntry[];
}

interface ViewTree {
  m_tree_name: string;
  m_config_name: string;
  m_groups: ViewGroup[];
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

function isWebBuild(): boolean {
  return typeof window !== "undefined" && !("__TAURI_INTERNALS__" in window);
}

// openGraphWindow / GraphMode / isGraphMode now live in utils/openGraphWindow
// (imported above for App's own use). Re-exported here so existing consumers
// (TigerViewPanel, tests) that import from "../App" keep working.
export { openGraphWindow, isGraphMode };
export type { GraphMode } from "./utils/openGraphWindow";

// openDataGridWindow / openDiagnosticsWindow now live in utils/openToolWindows
// (imported above for App's own use). Re-exported so existing consumers
// (TigerViewPanel, tests) that import from "../App" keep working.
export { openDataGridWindow, openDiagnosticsWindow };

/** Walk `treeData` to find the config_name (top-level node id) that owns the
 *  selected node, regardless of whether the selection is the turbine itself,
 *  a group inside it, or a view inside a group. Returns "" if no match. */
export function findConfigForTreeNode(
  treeData: TreeNode[],
  selectedId: string | null,
): string {
  if (!selectedId) return "";
  for (const turbineNode of treeData) {
    if (turbineNode.id === selectedId) return turbineNode.id;
    if (selectedId.startsWith(turbineNode.id + ":")) return turbineNode.id;
    for (const groupNode of turbineNode.children ?? []) {
      for (const viewNode of groupNode.children ?? []) {
        if (viewNode.id === selectedId) return turbineNode.id;
      }
    }
  }
  return "";
}

/** Capture the right pane (`.split-pane-right`) as a PNG. Used by File → Copy
 *  View and File → Print. Returns null if the pane isn't mounted or capture
 *  fails. */
async function captureRightPane(): Promise<{ blob: Blob; dataUrl: string } | null> {
  const target = document.querySelector<HTMLElement>(".split-pane-right");
  if (!target) {
    console.warn("captureRightPane: .split-pane-right not found");
    return null;
  }
  const { toBlob, toPng } = await import("html-to-image");
  // pixelRatio=1 keeps the image at the on-screen size; cacheBust avoids stale
  // image caching for any <img> inside the pane.
  const opts = { pixelRatio: 1, cacheBust: true };
  const blob = await toBlob(target, opts);
  const dataUrl = await toPng(target, opts);
  if (!blob || !dataUrl) return null;
  return { blob, dataUrl };
}

/** Copy the right pane to the system clipboard as a PNG. */
async function copyRightPaneToClipboard(): Promise<void> {
  const cap = await captureRightPane();
  if (!cap) return;
  await navigator.clipboard.write([new ClipboardItem({ "image/png": cap.blob })]);
}

/** Open a print dialog with the right pane image as the document. */
async function printRightPane(): Promise<void> {
  const cap = await captureRightPane();
  if (!cap) return;

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.left = "-9999px";
  iframe.style.width = "0";
  iframe.style.height = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return;
  }

  doc.open();
  doc.write(`<!doctype html><html><head><title>${getTigerResource("TIGER_UI_PRINT_VIEW_TITLE")}</title>
    <style>html,body{margin:0;padding:0;}img{display:block;max-width:100%;height:auto;}</style>
    </head><body><img src="${cap.dataUrl}" /></body></html>`);
  doc.close();

  const fire = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  };
  const img = doc.querySelector("img");
  if (img && !img.complete) {
    img.onload = fire;
  } else {
    fire();
  }
}

/** Open a file under `%TIGER%` (resolved server-side) with the OS default
 *  handler — used for menu entries that surface bundled docs/PDFs. */
async function openTigerFile(relative: string): Promise<void> {
  if (!isTauri()) {
    // Browser/webserver: the OS-handler launch can't work, so open the doc the
    // webserver streams from %TIGER% (e.g. manuals/tiger.pdf) in a new tab.
    window.open(`/api/tiger_file?relative=${encodeURIComponent(relative)}`, "_blank", "noopener");
    return;
  }
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("open_tiger_file", { relative });
}

/** Exit the application. In Tauri, calls the backend `exit_app` command
 *  (`app.exit(0)`) rather than closing the window — a window close would just
 *  re-enter the `onCloseRequested` hook that re-shows this dialog. In a browser
 *  context, attempts window.close() (typically a no-op). */
async function exitApplication(): Promise<void> {
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("exit_app");
  } else {
    window.close();
  }
}

/** Open a Save As dialog and write the right pane as a PNG file. In a browser
 *  context, falls back to a download via an anchor element. */
async function saveRightPaneAsPng(): Promise<void> {
  const cap = await captureRightPane();
  if (!cap) return;

  if (isTauri()) {
    const { save, message } = await import("@tauri-apps/plugin-dialog");
    const path = await save({
      defaultPath: await pickerSavePath(PickerKey.rightPanePng, "tiger-view.png"),
      filters: [{ name: getTigerResource("TIGER_GRAPH_PNG_IMAGE"), extensions: ["png"] }],
    });
    if (!path) return;
    rememberPickerDir(PickerKey.rightPanePng, path);
    try {
      const { writeFile } = await import("@tauri-apps/plugin-fs");
      const bytes = new Uint8Array(await cap.blob.arrayBuffer());
      await writeFile(path, bytes);
    } catch (e) {
      console.error("Save image failed:", e);
      await message(String(e), { title: getTigerResource("TIGER_GRAPH_SAVE_IMAGE"), kind: "error" });
    }
  } else {
    const url = URL.createObjectURL(cap.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tiger-view.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

function viewsTreeToNodes(trees: ViewTree[]): TreeNode[] {
  return trees.map((t) => ({
    id: t.m_config_name,
    label: t.m_tree_name,
    children: t.m_groups.map((g) => ({
      id: `${t.m_config_name}:${g.m_group_id}`,
      label: g.m_group_name,
      children: g.views.map((v) => ({
        id: v.m_view_id,
        label: v.m_view_name,
      })),
    })),
  }));
}

async function fetchAutomaticViewsExpansion(): Promise<boolean> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      return await invoke<boolean>("get_automatic_views_expansion");
    }
    const response = await fetch("/api/get_automatic_views_expansion");
    if (!response.ok) return true;
    const text = await response.text();
    if (!text) return true;
    return JSON.parse(text) as boolean;
  } catch (e) {
    console.error("Failed to fetch automatic_views_expansion:", e);
    return true; // C++ default is 1 (expanded)
  }
}

async function fetchViewsTree(): Promise<TreeNode[]> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      const trees = await invoke<ViewTree[]>("get_views_tree");
      return viewsTreeToNodes(trees);
    }
    const response = await fetch("/api/get_views_tree");
    if (!response.ok) return [];
    const text = await response.text();
    if (!text) return [];
    const trees: ViewTree[] = JSON.parse(text);
    return viewsTreeToNodes(trees);
  } catch (e) {
    console.error("Failed to fetch views tree:", e);
    return [];
  }
}

function App() {
  const [openDialogs, setOpenDialogs] = useState<Set<DialogKey>>(new Set());
  const [selectedTreeNode, setSelectedTreeNode] = useState<string | null>(null);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  // automatic_views_expansion (Mviews.ini) — start the views tree fully expanded
  // unless the site opts out. Defaults to true (C++ default).
  const [autoExpandTree, setAutoExpandTree] = useState(true);
  // Live diagnostics messages window (DV_ALARM): shown in the -live interface,
  // hidden/cleared from the Diagnostics menu.
  const [liveModeEnabled, setLiveModeEnabled] = useState(false);
  // One-shot pre-population for the Load Data dialog, set when a graph/diagnostics
  // window broadcasts a "Replay From" request. Cleared when the dialog closes.
  const [loadDataSeed, setLoadDataSeed] = useState<ReplayFromRequest | null>(null);
  const [diagMessagesVisible, setDiagMessagesVisible] = useState(true);
  const [diagClearSignal, setDiagClearSignal] = useState(0);
  // Snapshot of the tags currently displayed in the right pane. Used by
  // Tools → Data Grid to seed the new window without re-querying the layout.
  // A ref (not state) keeps the menu-action callback stable.
  const currentViewTagsRef = useRef<string[]>([]);
  const handleViewTagsChange = useCallback((tags: string[]) => {
    currentViewTagsRef.current = tags;
  }, []);

  // Web-build auth gate. Desktop (Tauri) builds skip this entirely.
  const [authReady, setAuthReady] = useState(!isWebBuild());
  const [authed, setAuthed] = useState(!isWebBuild());
  const [authMode, setAuthMode] = useState<AuthMode>("none");
  // Set when the user followed an emailed password-reset link. Read once at
  // mount, since LoginScreen strips the parameter from the URL afterwards.
  const [resetToken, setResetToken] = useState<string | null>(() =>
    isWebBuild()
      ? new URLSearchParams(window.location.search).get("reset_token")
      : null,
  );

  useEffect(() => {
    if (!isWebBuild()) return;
    (async () => {
      const mode = await fetchAuthMode();
      setAuthMode(mode);
      if (mode === "none") {
        setAuthed(true);
      } else {
        setAuthed(await isAuthenticated());
      }
      setAuthReady(true);
    })();
  }, []);

  // Mirror selection + tree into refs so the stable handleMenuAction can read
  // the current values without growing its dep list.
  const selectedTreeNodeRef = useRef<string | null>(null);
  const treeDataRef = useRef<TreeNode[]>([]);

  const handleTreeSelect = useCallback((id: string) => {
    setSelectedTreeNode(id);
    selectedTreeNodeRef.current = id;
  }, []);

  useEffect(() => {
    // Web build: the session cookie only exists after login, and the /api routes
    // are auth-gated. Don't fetch app data until authenticated — otherwise these
    // calls 401 on the first visit and, because the effect ran on mount with no
    // deps, never re-run after login, leaving the page blank until a refresh.
    // Gating on `authed` makes first-login fetch the same as a post-login reload.
    // (Desktop starts authed, so this still runs exactly once.)
    if (!authed) return;
    // The live/replay interface (-live / ?live=) always opens with the views
    // tree expanded; the plain alarm interface honours automatic_views_expansion
    // (Mviews.ini). Both flags are async (desktop -live needs a backend probe),
    // so resolve them together before settling the expansion state.
    Promise.all([getLiveModeEnabled(), fetchAutomaticViewsExpansion()])
      .then(([live, cfgExpand]) => {
        setLiveModeEnabled(live);
        setAutoExpandTree(autoExpandViewsTree(live, cfgExpand));
      })
      .catch(() => {});
    fetchViewsTree().then((data) => {
      // A -live tab (?live=<config>) shows only that turbine; the plain alarm
      // interface shows the whole site. (No-op outside the live tab.)
      const scoped = scopeTreeToConfig(data, getLiveConfig());
      setTreeData(scoped);
      treeDataRef.current = scoped;
      // On startup, load the first entry's title view. For the alarm interface
      // that's the site; for a -live tab it's the scoped turbine. TITLE_VIEW is a
      // section in the entry's Mviews.ini that get_widgets_list resolves directly
      // even though it isn't listed under a view group.
      if (scoped.length > 0) {
        const titleViewId = `${scoped[0].id}:TITLE_VIEW`;
        setSelectedTreeNode(titleViewId);
        selectedTreeNodeRef.current = titleViewId;
      }
    });
    // Serve offscreen graph renders for XML report generation (TGraph).
    initReportGraphRenderer().catch((e) =>
      console.error("failed to init report graph renderer", e),
    );
  }, [authed]);

  // A web -live tab (?live=<config>) opens in the site's configured default mode
  // (live or replay). That default is seeded server-side onto the tab's distinct
  // session the first time it's seen (auth/routes.rs require_auth →
  // ti_connect::ensure_session_view_mode), so getViewRunState already returns the
  // right starting mode for every window with no client-side flip needed.

  // Intercept the main window's close button (top-right X): always prevent the
  // close and show the exit-confirmation dialog (the same one as File → Exit).
  // The actual exit goes through ExitAppDialog's OK → exitApplication() →
  // backend `exit_app` (app.exit(0)), never a window close, so this hook is not
  // re-entered. Cancel leaves the window open. Tool windows are unaffected (this
  // only runs in the main window, where App is mounted).
  useEffect(() => {
    if (!isTauri()) return;
    let unlisten: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      const { getCurrentWindow } = await import("@tauri-apps/api/window");
      const fn = await getCurrentWindow().onCloseRequested((event) => {
        event.preventDefault();
        setOpenDialogs((prev) => new Set(prev).add("exitApp"));
      });
      if (cancelled) fn();
      else unlisten = fn;
    })().catch((e) => console.error("failed to register close handler", e));
    return () => {
      cancelled = true;
      if (unlisten) unlisten();
    };
  }, []);

  const openDialog = (key: DialogKey) => {
    setOpenDialogs((prev) => new Set(prev).add(key));
  };

  const closeDialog = (key: DialogKey) => {
    setOpenDialogs((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const isOpen = (key: DialogKey) => openDialogs.has(key);
  const close = (key: DialogKey) => () => closeDialog(key);

  // A graph or diagnostics tool window asked to replay from a point. Seed the
  // Load Data dialog with the turbine + window and open it, then bring the main
  // window forward (the request came from a tool window that may be on top).
  useEffect(() => {
    if (!isTauri()) return;
    let unlisten: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      const [{ listen }, { getCurrentWindow }] = await Promise.all([
        import("@tauri-apps/api/event"),
        import("@tauri-apps/api/window"),
      ]);
      const fn = await listen<ReplayFromRequest>(LOAD_DATA_REPLAY_EVENT, (e) => {
        setLoadDataSeed(e.payload);
        // "Replay From" on a live diagnostics/trend window is a replay action,
        // like File > Load Data: flip the session to Replay first (only if it's
        // still live) so the views stop following live data before the seeded
        // dialog opens. Skipped when already replaying to avoid re-broadcasting
        // view-mode-changed, which would blank + refetch the embedded graphs.
        void getViewRunState().then((s) => {
          if (s === "live") {
            setViewMode(false).catch((err) => console.error("switch to replay failed:", err));
          }
        });
        openDialog("loadData");
        void getCurrentWindow().setFocus();
      });
      if (cancelled) fn();
      else unlisten = fn;
    })().catch((err) => console.error("failed to register replay-from listener", err));
    return () => {
      cancelled = true;
      if (unlisten) unlisten();
    };
  }, []);

  // Context for the report parameter dialogs (which custom report was clicked
  // and what it needs), set when a Tools > Reports action is dispatched.
  const [reportCtx, setReportCtx] = useState({
    configName: "",
    menuConfigName: "",
    reportName: "",
    modelName: "",
    reportFile: "",
    withState: false,
  });
  // Busy dialog shown while TigerExcelReports.exe runs (C++
  // Ui_progress_info_dialog_start equivalent).
  const [reportBusy, setReportBusy] = useState(false);
  // Busy dialog while the import daemon processes a CSD file.
  const [importCsdBusy, setImportCsdBusy] = useState(false);
  // Busy dialog while the alarm-server interface establishes its ORB connection
  // at startup. Open from the first render on the desktop, held through the
  // startup version check, and closed once the backend confirms the connection
  // (`server-connected`) or a version-update dialog takes over. See
  // useServerConnecting for why it must start open rather than wait for a query.
  const serverConnecting = useServerConnecting();
  // What that wait is actually doing — the ORB connect, the tunnel probe, then
  // one line per turbine as the version check works through them. Startup can
  // run to minutes on a remote site, and a bare spinner cannot be told from a
  // hang.
  const startupSteps = useStartupProgress();

  const handleMenuAction = useCallback((action: string) => {
    const actionMap: Record<string, DialogKey> = {
      // File menu items
      "file.export.data": "export",
      "file.export.trend": "exportTrend",
      "file.backupData": "backupData",
      "file.restoreData": "restoreData",
      "file.exit": "exitApp",
      "file.changePassword": "changePassword",
      // Tools menu
      // "tools.dataGrid" handled separately as a new window
      // "tools.graph.data" handled separately as a new window
      // "tools.graph.multiTime" handled separately as a new window
      // Tools > Trend
      // "tools.trend.multiXY" handled separately as a new window
      // Diagnostics menu
      // "diagnostics.view" handled separately as a new window
      "diagnostics.filters": "diagFilter",
      // Window menu
      "window.toolSets": "toolSet",
      // Help menu
      "help.about": "about",
    };

    // Windows that open as separate OS windows. They open empty, so default
    // their Add-Channel dialog to the currently-selected turbine (the views-tree
    // selection — the C++ Ti_get_current_turbine()) instead of list[0]. Matters
    // on the multi-turbine webserver; "" (nothing selected) falls back to list[0].
    const currentConfig = findConfigForTreeNode(treeDataRef.current, selectedTreeNodeRef.current);
    if (action === "tools.dataGrid") {
      openDataGridWindow(currentViewTagsRef.current, undefined, currentConfig);
      return;
    }
    if (action === "diagnostics.view") {
      openDiagnosticsWindow(currentConfig);
      return;
    }
    if (action === "tools.graph.data") {
      openGraphWindow("graph", undefined, undefined, undefined, currentConfig);
      return;
    }
    if (action === "tools.trend.graph") {
      openGraphWindow("trend", undefined, undefined, undefined, currentConfig);
      return;
    }
    if (action === "tools.graph.event") {
      openGraphWindow("event", undefined, undefined, undefined, currentConfig);
      return;
    }
    if (action === "tools.graph.multiTime") {
      openGraphWindow("multiTime", undefined, undefined, undefined, currentConfig);
      return;
    }
    if (action === "tools.graph.xy") {
      openGraphWindow("xy", undefined, undefined, undefined, currentConfig);
      return;
    }
    if (action === "tools.trend.xy") {
      openGraphWindow("xyTrend", undefined, undefined, undefined, currentConfig);
      return;
    }
    if (action === "tools.trend.multiXY") {
      openGraphWindow("xyTrendMulti", undefined, undefined, undefined, currentConfig);
      return;
    }
    if (action === "window.showAll") {
      showAllToolWindows().catch((e) => console.error("Show All Tool Windows failed:", e));
      return;
    }

    if (action === "window.closeAll") {
      closeAllToolWindows().catch((e) => console.error("Close All Tool Windows failed:", e));
      return;
    }

    if (action === "window.save") {
      // C++ OnApp_tool_wins_save_button → Ui_tool_win_save. generateToolReport
      // prompts for the output file itself: a native save dialog on the desktop,
      // ReportSaveDialog in the browser.
      generateToolReport(setReportBusy).catch((e) =>
        console.error("Save Tools as Report failed:", e),
      );
      return;
    }

    if (action === "file.copyView") {
      copyRightPaneToClipboard().catch((e) => console.error("Copy View failed:", e));
      return;
    }

    if (action === "file.print") {
      printRightPane().catch((e) => console.error("Print failed:", e));
      return;
    }

    if (action === "file.saveView") {
      saveRightPaneAsPng().catch((e) => console.error("Save View failed:", e));
      return;
    }

    if (action === "file.loadData") {
      openDialog("loadData");
      return;
    }

    if (action === "diagnostics.clearMessages") {
      setDiagClearSignal((s) => s + 1);
      return;
    }
    if (action === "diagnostics.toggleMessages") {
      setDiagMessagesVisible((v) => !v);
      return;
    }

    if (action === "advanced.selectDataFile") {
      // C++ OnApp_advanced_file_replay_button: pick binary data file(s) and replay.
      (async () => {
        if (!isTauri()) return;
        const { open } = await import("@tauri-apps/plugin-dialog");
        // Seed from the data folder of the current turbine (C++ uses
        // Ti_get_current_turbine); fall back to the first configured turbine.
        const config = findConfigForTreeNode(treeData, selectedTreeNode) || treeData[0]?.id || "";
        const selected = await open({
          multiple: true,
          defaultPath: await pickerInitialDir(PickerKey.selectDataFile, config),
          filters: [{ name: getTigerResource("TIGER_UI_TIGER_BINARY_FILES"), extensions: ["gz"] }],
        });
        const files = Array.isArray(selected) ? selected : selected ? [selected] : [];
        if (files.length === 0) return;
        rememberPickerDir(PickerKey.selectDataFile, files);
        await replayDataFiles(files);
      })().catch((e) => console.error("Select Data File failed:", e));
      return;
    }

    if (action === "file.importCsd") {
      // C++ OnApp_import_csd_button → Ti_import_csd_file.
      importCsdFile(setImportCsdBusy).catch((e) => console.error("Import CSD failed:", e));
      return;
    }

    if (action === "help.tigerManual") {
      openTigerFile("manuals/tiger.pdf").catch((e) => console.error("Open Tiger Manual failed:", e));
      return;
    }

    if (action === "help.turbineManuals") {
      // The entry is only shown when a manual is present; resolve its path
      // (C++ Ti_tiger_turbine_manual) and open it with the default handler. On
      // web the path comes from /api/get_turbine_manual and opens via the
      // openTigerFile web path (/api/tiger_file in a new tab).
      (async () => {
        try {
          let relative: string | null;
          if (isTauri()) {
            const { invoke } = await import("@tauri-apps/api/core");
            relative = await invoke<string | null>("get_turbine_manual");
          } else {
            const resp = await fetch("/api/get_turbine_manual");
            relative = resp.ok ? ((await resp.json()) as string | null) : null;
          }
          if (relative) await openTigerFile(relative);
        } catch (e) {
          console.error("Open Turbine Manual failed:", e);
        }
      })();
      return;
    }

    // Tools > Start Tiger > <turbine>: same launch as an ALARM_START_TIGER view
    // button (desktop spawns the turbine's display process, web opens a ?live= tab).
    if (action.startsWith("tools.startTiger.")) {
      const configName = action.slice("tools.startTiger.".length);
      startTiger(configName).catch((e) => console.error("Start Tiger failed:", e));
      return;
    }

    if (action.startsWith("tools.report.")) {
      const parsed = parseReportAction(action);
      if (!parsed) return;
      if (parsed.kind === "custom") {
        // The report's ini section says which parameter dialog it needs
        // (report_menu_item_cls::check_and_perform).
        (async () => {
          const info = await fetchCustomReportInfo(parsed.configName, parsed.name);
          const choice = dialogForReportModel(info.model_name);
          const ctx = {
            configName: info.report_config_name || parsed.configName,
            menuConfigName: parsed.configName,
            reportName: parsed.name,
            modelName: info.model_name,
            reportFile: info.report_file,
            withState: choice?.withState ?? false,
          };
          if (!choice) {
            // No parameter dialog — generate over the last-used window, like
            // the C++ falling straight through to the save prompt.
            await generateCustomReport(ctx, reportWindowForRange(getReportRange()), setReportBusy);
            return;
          }
          setReportCtx(ctx);
          openDialog(choice.dialog);
        })().catch((e) => console.error("custom report failed:", e));
        return;
      }
      if (parsed.kind === "controller") {
        // C++ OnApp_controller_report_button: save prompt → snapshot document.
        generateControllerReport(parsed.configName, setReportBusy).catch((e) =>
          console.error("controller report failed:", e),
        );
        return;
      }
      if (parsed.kind === "stability") {
        // C++ OnApp_stability_report_button: save prompt → performance
        // server request → stability document.
        generateStabilityReport(parsed.configName, setReportBusy).catch((e) =>
          console.error("stability report failed:", e),
        );
        return;
      }
      if (parsed.kind === "event") {
        // C++ event_menu_item_cls::check_and_perform: save prompt → alarm
        // server request → tab-separated report.
        generateEventReport(parsed.configName, parsed.name, setReportBusy).catch((e) =>
          console.error("event report failed:", e),
        );
        return;
      }
      // The remaining built-ins have no parameter dialog: they go straight to a
      // save prompt or an async backend request (not yet wired).
      console.log("Menu action (not yet wired):", action);
      return;
    }

    // Web only: drop the server-side session and fall back to the login screen.
    // Reloading also clears every in-memory view/tool state belonging to the
    // signed-out user, so the next login starts clean.
    if (action === "file.signOut") {
      void logout().then(() => window.location.reload());
      return;
    }

    // File > Theme: UI chrome theme (dialogs/menus). Persisted + applied by the
    // theme module; other windows follow via the storage event.
    if (action.startsWith("file.uitheme.")) {
      const id = action.slice("file.uitheme.".length);
      if (isUiThemeId(id)) setUiTheme(id);
      return;
    }

    const target = actionMap[action];
    if (target) {
      openDialog(target);
    } else {
      console.log("Menu action (not yet wired):", action);
    }
  }, []);

  if (!authReady) return null;
  // A reset link diverts to LoginScreen even for an already-signed-in browser —
  // the token is valid and the user clearly means to use it. Only when the mode
  // actually supports reset, so a stray ?reset_token= cannot lock out a
  // none/ad_auth site.
  const resetPending =
    !!resetToken && (authMode === "db_auth" || authMode === "mfa_auth");
  if (!authed || resetPending) {
    return (
      <LoginScreen
        mode={authMode}
        onAuthed={() => setAuthed(true)}
        resetToken={resetToken ?? undefined}
        onResetFinished={() => setResetToken(null)}
      />
    );
  }

  return (
    <div className="app-container">
      <MenuBar
        onMenuAction={handleMenuAction}
        changePasswordEnabled={authMode === "db_auth" || authMode === "mfa_auth"}
        signOutEnabled={isWebBuild() && authMode !== "none"}
      />

      <SplitPane
        left={
          <TreeView
            key={`expand-${autoExpandTree}`}
            nodes={treeData}
            selectedId={selectedTreeNode}
            onSelect={handleTreeSelect}
            autoExpand={autoExpandTree}
          />
        }
        right={<TigerViewPanel viewId={selectedTreeNode} onTagsChange={handleViewTagsChange} />}
      />

      <DiagnosticsMessagesPanel
        visible={liveModeEnabled && diagMessagesVisible}
        clearSignal={diagClearSignal}
        // Currently-selected turbine (C++ Ti_get_current_turbine). On a web
        // ?live=<config> tab we already know the turbine from the URL, so seed from
        // it until the views tree loads — otherwise the panel briefly shows every
        // turbine's alarms (the empty-config fallback) before it scopes.
        config={findConfigForTreeNode(treeData, selectedTreeNode) || getLiveConfig() || ""}
      />

      {/* All dialogs */}
      <DiagnosticsQueryDialog open={isOpen("diagnosticsQuery")} onClose={close("diagnosticsQuery")} />
      <ExportDialog open={isOpen("export")} onClose={close("export")} variant="data" />
      <ExportDialog open={isOpen("exportTrend")} onClose={close("exportTrend")} variant="trend" />
      <BackupDataDialog open={isOpen("backupData")} onClose={close("backupData")} />
      <RestoreDataDialog
        open={isOpen("restoreData")}
        onClose={close("restoreData")}
        config={findConfigForTreeNode(treeData, selectedTreeNode) || treeData[0]?.id || ""}
      />
      {/* Data Grid opens as a separate window via openDataGridWindow() */}
      {/* Diagnostics opens as a separate window via openDiagnosticsWindow() */}
      {/* Graph opens as a separate window via openGraphWindow() */}
      <GraphChannelSelectionDialog open={isOpen("graphChannelSelection")} onClose={close("graphChannelSelection")} />
      <DiagFilterDialog
        open={isOpen("diagFilter")}
        onClose={close("diagFilter")}
        // Live/replay interface: scope the filter list to the currently-selected
        // turbine, resolved the same way as the DV_ALARM messages panel above.
        // The plain alarm interface passes "" and lists every turbine.
        config={
          liveModeEnabled
            ? findConfigForTreeNode(treeData, selectedTreeNode) || getLiveConfig() || ""
            : ""
        }
      />
      <DiagSelectTagDialog open={isOpen("diagSelectTag")} onClose={close("diagSelectTag")} />
      <LoadDataDialog
        open={isOpen("loadData")}
        seed={loadDataSeed}
        onClose={() => { setLoadDataSeed(null); closeDialog("loadData"); }}
        onOk={(turbine, _event, timeRange) => {
          // Tiger seconds (1901 epoch) — what the data server / load_replay_data
          // expect, matching the graph HIST queries. Unix seconds here loaded a
          // window ~69 years off.
          const start = dateTimeToTigerSeconds(timeRange.startDate, timeRange.startTime);
          const end = dateTimeToTigerSeconds(timeRange.endDate, timeRange.endTime);
          if (start == null || end == null) return;
          loadReplayData(turbine, start, end).catch((e) =>
            console.error("load_replay_data failed:", e),
          );
        }}
      />
      <HistDiagSelectTagDialog open={isOpen("histDiagSelectTag")} onClose={close("histDiagSelectTag")} />
      <HistTrendDiagSelectTagDialog open={isOpen("histTrendDiagSelectTag")} onClose={close("histTrendDiagSelectTag")} />
      <ReportTimeDialog
        open={isOpen("reportTime")}
        onClose={close("reportTime")}
        configName={reportCtx.configName}
        modelName={reportCtx.modelName}
        onOk={(range) => {
          generateCustomReport(reportCtx, reportWindowForRange(range), setReportBusy).catch((e) =>
            console.error("report generation failed:", e),
          );
        }}
      />
      <ReportDateDialog
        open={isOpen("reportDate")}
        onClose={close("reportDate")}
        configName={reportCtx.configName}
        withState={reportCtx.withState}
        onOk={(startDate, endDate, turbineState) => {
          // The C++ stamps the day bounds onto the picked dates; the DATE_STATE
          // turbine state feeds the XML engine's reportState substitution.
          const window = reportWindowForRange(
            { startDate, startTime: "00:00:00", endDate, endTime: "23:59:59" },
            turbineState,
          );
          generateCustomReport(reportCtx, window, setReportBusy).catch((e) =>
            console.error("report generation failed:", e),
          );
        }}
      />
      <ReportYearDialog
        open={isOpen("reportYear")}
        onClose={close("reportYear")}
        onOk={(year) => {
          generateCustomReport(reportCtx, reportWindowForYear(year), setReportBusy).catch((e) =>
            console.error("report generation failed:", e),
          );
        }}
      />
      <ReportMonthDialog
        open={isOpen("reportMonth")}
        onClose={close("reportMonth")}
        onOk={(month, year) => {
          generateCustomReport(reportCtx, reportWindowForMonth(month, year), setReportBusy).catch(
            (e) => console.error("report generation failed:", e),
          );
        }}
      />
      <UIProgressDialog open={isOpen("uiProgress")} onClose={close("uiProgress")} />
      {/* Busy indicator while the Excel report engine fills a template (the
          download progress dialog mounts after this one, so a data request the
          report starts stays visible over it). */}
      <UIProgressDialog
        open={reportBusy}
        onClose={() => {}}
        title={getTigerResource("TIGER_REPORT_SUCCESS_TITLE")}
        message={getTigerResource("TIGER_REPORT_PROGRESS")}
      />
      {/* Busy indicator while the import daemon processes a CSD file (the
          C++ Ui_progress_info_dialog_start("IMPORT_CSD") literals). */}
      <UIProgressDialog
        open={importCsdBusy}
        onClose={() => {}}
        title={getTigerResource("TIGERUI_IMPORT_CSD")}
        message={getTigerResource("TIGER_UI_IMPORT_CSD_MSG")}
      />
      {/* Busy indicator while the alarm-server interface connects to the ORB
          at startup, held through the startup version check (cleared by the
          backend's server-connected event or by the version-update dialog
          taking over). */}
      <UIProgressDialog
        open={serverConnecting}
        onClose={() => {}}
        title={getTigerResource("TIGER_UI_CONNECTING_SERVER_TITLE")}
        message={getTigerResource("TIGER_UI_CONNECTING_SERVER_MSG")}
        steps={startupSteps}
      />
      <AboutDialog open={isOpen("about")} onClose={close("about")} />
      <ExitAppDialog
        open={isOpen("exitApp")}
        onClose={close("exitApp")}
        onOk={() => { exitApplication().catch((e) => console.error("Exit failed:", e)); }}
      />
      <ChangePasswordDialog
        open={isOpen("changePassword")}
        onClose={close("changePassword")}
      />
      <ToolSetDialog open={isOpen("toolSet")} onClose={close("toolSet")} />
      <VersionInfoDialog open={isOpen("versionInfo")} onClose={close("versionInfo")} />
      <RestartUIDialog open={isOpen("restartUI")} onClose={close("restartUI")} />
      <DataRequestDialog />
      <VersionUpdateDialog />
      <LicenseErrorDialog />
      <ReplayLoadDialog />
      <ServerReplayDialog />
      <HistoricalProgressDialog />
      {/* Mounted after the replay/historical progress cards on purpose: all
          overlays share z-index 1200, so mount order decides stacking. A replay
          load that has to download data shows both cards at once, and the
          download one (with the transfer rate and its own Cancel) must be the
          one on top, not buried behind "Loading replay data". */}
      <DownloadProgressDialog />
      <ExportProgressDialog />
      <BackupRestoreProgressDialog />
    </div>
  );
}

export default App;
